import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { startOfMonth, endOfMonth, eachDayOfInterval, format, addMonths, subMonths, getDay, parseISO, isSameDay, isSameMonth } from "date-fns";
import { pt } from "date-fns/locale";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MemberHeader from "@/components/MemberHeader";
import SuggestPlanDialog from "@/components/SuggestPlanDialog";
import AuthImage from "@/components/AuthImage";
import { Calendar, MapPin, Users, Euro, Repeat, ArrowUpRight, CalendarDays, LayoutGrid, ChevronLeft, ChevronRight, UserCircle, MessageCircle, Ban, Trash2, Pencil } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

function money(v) {
  if (v == null || v === 0) return "Gratuito";
  return `${Number(v).toFixed(2)} €`;
}

function stateOf(plan) {
  if (plan.joined) return "joined";
  if (plan.is_full) return "full";
  return "available";
}
const STATE_STYLE = {
  joined: "bg-[#1E5E2A] text-white",
  available: "bg-[#1E5E2A] text-white",
  full: "bg-[#8C2222] text-white",
};

export function PlanCard({ plan, index, isAdmin = false, onDeleted, categories = [] }) {
  const st = stateOf(plan);
  const handleDelete = async () => {
    try {
      await api.delete(`/admin/plans/${plan.id}`);
      toast.success("Plano apagado.");
      onDeleted?.();
    } catch {
      toast.error("Não foi possível apagar o plano.");
    }
  };
  const badge = st === "full" ? (
    <span data-testid="badge-soldout" className="text-xs font-semibold px-3 py-1 rounded-full bg-[#F9ECEC] text-[#8C2222]">Esgotado</span>
  ) : st === "joined" ? (
    <span data-testid="badge-joined" className="text-xs font-semibold px-3 py-1 rounded-full bg-[#EBF4EC] text-[#1E5E2A]">Inscrita</span>
  ) : (
    <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#EBF4EC] text-[#1E5E2A]">Disponível</span>
  );
  const pct = plan.seats ? Math.min(100, Math.round((plan.taken / plan.seats) * 100)) : 0;
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.05 }} className="relative">
      {isAdmin && (
        <div className="absolute bottom-4 right-4 z-20 flex gap-2">
          <SuggestPlanDialog categories={categories} isAdmin editPlan={plan} onCreated={onDeleted} trigger={
            <button data-testid={`edit-plan-card-${plan.id}`} onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5 bg-[#FCFCFA]/95 backdrop-blur-sm border border-[#48020C] text-[#48020C] hover:bg-[#48020C] hover:text-white rounded-full px-3 py-1.5 text-xs shadow-sm transition-colors">
              <Pencil className="w-3.5 h-3.5" /> Editar
            </button>
          } />
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid={`delete-plan-card-${plan.id}`} onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1.5 bg-[#FCFCFA]/95 backdrop-blur-sm border border-[#8C2222] text-[#8C2222] hover:bg-[#8C2222] hover:text-white rounded-full px-3 py-1.5 text-xs shadow-sm transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> Apagar
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#FCFCFA] border-[#E3DED2]" onClick={(e) => e.stopPropagation()}>
              <AlertDialogHeader>
                <AlertDialogTitle className="font-serif text-2xl text-[#26130E]">Apagar plano?</AlertDialogTitle>
                <AlertDialogDescription className="text-[#6E5F58]">Vais apagar <strong>{plan.title}</strong> ({plan.date}). As inscrições e o chat deste plano serão removidos. Esta ação é irreversível.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-full border-[#E3DED2]">Cancelar</AlertDialogCancel>
                <AlertDialogAction data-testid={`confirm-delete-plan-card-${plan.id}`} onClick={handleDelete} className="rounded-full bg-[#8C2222] hover:bg-[#6f1a1a] text-white">Apagar plano</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
      <Link to={`/area/plano/${plan.id}`} data-testid={`plan-card-${plan.id}`}
        className="group block sc-card sc-card-hover overflow-hidden h-full">
        {plan.photo_path ? (
          <div className="relative">
            <AuthImage path={plan.photo_path} className="w-full h-44 object-cover transition-transform duration-700 group-hover:scale-[1.04]" alt={plan.title} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#26130E]/75 via-[#26130E]/10 to-transparent" />
            <span className="absolute top-3 left-3 sc-label text-[#F7EEC7] bg-[#26130E]/50 backdrop-blur-sm rounded-full px-3 py-1">{plan.category}</span>
            <span className="absolute top-3 right-3">{badge}</span>
          </div>
        ) : null}
        <div className="p-6 sm:p-7">
          {!plan.photo_path && (
            <div className="flex items-start justify-between gap-3">
              <span className="sc-label text-[#48020C]">{plan.category}</span>
              {badge}
            </div>
          )}
          <h3 className="font-serif text-2xl text-[#26130E] mt-3 leading-snug flex items-start gap-2">
            {plan.title}
            <ArrowUpRight className="w-5 h-5 mt-1.5 text-[#48020C] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
          </h3>
          <div className="mt-5 space-y-2 text-sm text-[#6E6359]">
            <p className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#48020C]" /> {plan.date}{plan.time ? ` · ${plan.time}` : ""}</p>
            <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#48020C]" /> {plan.location}</p>
            <p className="flex items-center gap-2"><Euro className="w-4 h-4 text-[#48020C]" /> {money(plan.value)}</p>
            {plan.recurrence !== "pontual" && (
              <p className="flex items-center gap-2 text-[#48020C]"><Repeat className="w-4 h-4" /> {plan.recurrence === "semanal" ? "Semanal" : "Mensal"}</p>
            )}
            {plan.created_by_name && (
              <p className="flex items-center gap-2 text-[#6E5F58] pt-1"><UserCircle className="w-4 h-4 text-[#48020C]" /> Criado por {plan.created_by_name}</p>
            )}
          </div>
          <div className="mt-5">
            <div className="h-1.5 bg-[#E3DED2] rounded-full overflow-hidden">
              <div className={`h-full transition-all duration-700 ${st === "full" ? "bg-[#8C2222]" : "bg-[#48020C]"}`} style={{ width: `${pct}%` }} />
            </div>
            <p className="flex items-center gap-2 text-xs text-[#6E5F58] mt-2"><Users className="w-3.5 h-3.5 text-[#48020C]" /> {plan.taken}/{plan.seats} lugares preenchidos</p>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

function MonthCalendar({ plans }) {
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const days = useMemo(() => {
    const start = startOfMonth(cursor);
    const end = endOfMonth(cursor);
    const all = eachDayOfInterval({ start, end });
    const lead = (getDay(start) + 6) % 7; // Monday-first
    return { all, lead };
  }, [cursor]);

  const plansByDay = (day) => plans.filter((p) => {
    try { return isSameDay(parseISO(p.date), day); } catch { return false; }
  });

  const weekdays = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

  return (
    <div data-testid="member-calendar" className="sc-card p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6">
        <button data-testid="cal-prev" onClick={() => setCursor(subMonths(cursor, 1))} className="w-10 h-10 rounded-full border border-[#E3DED2] flex items-center justify-center hover:bg-[#ECE7DC] transition-colors"><ChevronLeft className="w-5 h-5 text-[#48020C]" /></button>
        <h3 className="font-serif text-2xl sm:text-3xl text-[#26130E] capitalize">{format(cursor, "MMMM yyyy", { locale: pt })}</h3>
        <button data-testid="cal-next" onClick={() => setCursor(addMonths(cursor, 1))} className="w-10 h-10 rounded-full border border-[#E3DED2] flex items-center justify-center hover:bg-[#ECE7DC] transition-colors"><ChevronRight className="w-5 h-5 text-[#48020C]" /></button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-5 text-xs text-[#6E6359]">
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#1E5E2A]" /> Disponível</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#8C2222]" /> Esgotado</span>
      </div>

      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {weekdays.map((w) => <div key={w} className="text-center sc-label text-[#6E5F58] py-2">{w}</div>)}
        {Array.from({ length: days.lead }).map((_, i) => <div key={`e${i}`} />)}
        {days.all.map((day) => {
          const dayPlans = plansByDay(day);
          const today = isSameDay(day, new Date());
          return (
            <div key={day.toISOString()} className={`min-h-[74px] sm:min-h-[92px] rounded-lg border p-1.5 transition-colors ${today ? "border-[#48020C] ring-1 ring-[#48020C] bg-[#FCFCFA]" : "border-[#E3DED2] bg-[#FCFCFA]/60 hover:bg-[#FCFCFA]"}`}>
              <div className={`text-xs mb-1 ${today ? "text-[#48020C] font-semibold" : "text-[#6E5F58]"}`}>{format(day, "d")}</div>
              <div className="space-y-1">
                {dayPlans.slice(0, 3).map((p) => (
                  <Link key={p.id} to={`/area/plano/${p.id}`} data-testid={`cal-plan-${p.id}`}
                    className={`block truncate text-[10px] sm:text-xs rounded px-1.5 py-0.5 ${STATE_STYLE[stateOf(p)]} hover:opacity-90 transition-opacity`}>
                    {p.title}
                  </Link>
                ))}
                {dayPlans.length > 3 && <span className="text-[10px] text-[#6E5F58]">+{dayPlans.length - 3}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MemberArea() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const blocked = user?.blocked_until && new Date(user.blocked_until) > new Date();
  const [plans, setPlans] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("Todos");
  const [view, setView] = useState("calendar");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [{ data: p }, { data: c }] = await Promise.all([
        api.get("/plans", { params: filter === "Todos" ? {} : { category: filter } }),
        api.get("/plans/categories"),
      ]);
      setPlans(p);
      setCategories(c);
    } catch {
      toast.error("Não foi possível carregar os planos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div className="min-h-screen sc-paper">
      <MemberHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <div className="sc-label text-[#48020C]">Olá, {user?.name?.split(" ")[0]} {user?.is_founding && "· Membro Fundadora"}</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#26130E] mt-2">Agenda</h1>
            <p className="text-[#6E5F58] mt-3 max-w-md">Os próximos encontros da comunidade, no Porto. Escolhe o teu e junta-te.</p>
          </div>
          <SuggestPlanDialog categories={categories} isAdmin={isAdmin} onCreated={load} />
        </div>

        {user?.status === "approved" && (
          <a href="https://chat.whatsapp.com/IeG4Eyk16H39xWSSFL8s4p" target="_blank" rel="noopener noreferrer" data-testid="whatsapp-community-link"
            className="group mt-8 flex items-center gap-4 bg-[#48020C] hover:bg-[#26130E] text-[#FCFCFA] rounded-2xl px-6 py-5 transition-colors sc-shadow-soft">
            <span className="flex-shrink-0 w-11 h-11 rounded-full bg-[#FCFCFA]/10 flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-[#F7EEC7]" />
            </span>
            <div className="flex-1">
              <div className="sc-label text-[#F7EEC7]">O CLUBE, TODOS OS DIAS</div>
              <div className="font-serif text-xl sm:text-2xl mt-0.5">Entra na comunidade do WhatsApp</div>
            </div>
            <ArrowUpRight className="w-5 h-5 text-[#F7EEC7] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-10">
          <div className="flex flex-wrap gap-2" data-testid="category-filters">
            {["Todos", ...categories].map((c) => (
              <button key={c} data-testid={`filter-${c}`} onClick={() => setFilter(c)}
                className={`text-sm rounded-full px-5 py-2 transition-colors border ${filter === c ? "bg-[#48020C] text-[#FCFCFA] border-[#48020C]" : "bg-transparent text-[#6E6359] border-[#E3DED2] hover:border-[#48020C]"}`}>
                {c}
              </button>
            ))}
          </div>
          <div className="flex gap-1 bg-[#F4F1EA] border border-[#E3DED2] rounded-full p-1 self-start">
            <button data-testid="view-calendar" onClick={() => setView("calendar")} className={`flex items-center gap-2 text-sm rounded-full px-4 py-2 transition-colors ${view === "calendar" ? "bg-[#48020C] text-[#FCFCFA]" : "text-[#6E6359]"}`}><CalendarDays className="w-4 h-4" /> Calendário</button>
            <button data-testid="view-list" onClick={() => setView("list")} className={`flex items-center gap-2 text-sm rounded-full px-4 py-2 transition-colors ${view === "list" ? "bg-[#48020C] text-[#FCFCFA]" : "text-[#6E6359]"}`}><LayoutGrid className="w-4 h-4" /> Lista</button>
          </div>
        </div>

        {blocked && (
          <div data-testid="blocked-banner" className="mt-8 flex items-start gap-4 bg-[#F9ECEC] border border-[#8C2222]/30 rounded-2xl px-6 py-5">
            <Ban className="w-6 h-6 text-[#8C2222] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-serif text-xl text-[#8C2222]">Estás bloqueada temporariamente</div>
              <p className="text-sm text-[#6E5F58] mt-1">Não podes aderir a planos durante este período. Fica atenta — o acesso será reposto em breve.</p>
            </div>
          </div>
        )}

        <div className={blocked ? "pointer-events-none select-none opacity-50" : ""}>
        {loading ? (
          <p className="text-[#6E6359] mt-16 text-center">A carregar…</p>
        ) : view === "calendar" ? (
          <div className="mt-8">
            <MonthCalendar plans={plans} />
          </div>
        ) : plans.length === 0 ? (
          <div className="text-center py-24">
            <p className="font-serif text-2xl text-[#48020C]">Ainda não há planos nesta categoria.</p>
            <p className="text-[#6E6359] mt-2">Sê a primeira a sugerir um!</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {plans.map((p, i) => <PlanCard key={p.id} plan={p} index={i} isAdmin={isAdmin} onDeleted={load} categories={categories} />)}
          </div>
        )}
        </div>
      </main>
    </div>
  );
}
